import React from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdLocationOn } from "react-icons/md";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { EnrichedSupplierForDelivery, VehicleTypeCost } from "../../types";
import {
  getDefaultSelectedVehicleType,
  getVehicleTypeCostsFromSplit,
  getVehicleTypeDisplay,
  getVehicleTypeIcon,
} from "../../utils/vehicleTypeUtils";

export interface SupplierSplitHeaderProps {
  displayTitle: string;
  supplier: EnrichedSupplierForDelivery;
  isRemainingItems: boolean;
  supplierFulfillingItemsMRP: number;
  actions: React.ReactNode;
  isReadOnly?: boolean;
  onSelectedVehicleTypeChange?: (vehicleType: string) => void;
}

export const SupplierSplitHeader: React.FC<SupplierSplitHeaderProps> = ({
  displayTitle,
  supplier,
  isRemainingItems,
  supplierFulfillingItemsMRP,
  actions,
  isReadOnly = false,
  onSelectedVehicleTypeChange,
}) => {
  const vehicleTypeOptions = getVehicleTypeCostsFromSplit(supplier);
  const selectedVehicleType =
    supplier.selectedVehicleType ?? getDefaultSelectedVehicleType(supplier);
  const selectedEntry =
    vehicleTypeOptions.find(
      (entry) => entry.vehicleType === selectedVehicleType,
    ) ?? vehicleTypeOptions[0];
  const selectedIcon =
    getVehicleTypeIcon(selectedEntry?.wheelClass) ??
    getVehicleTypeDisplay(supplier.vehicleType)?.icon;
  const selectedLabel =
    selectedEntry?.label ||
    getVehicleTypeDisplay(supplier.vehicleType)?.label ||
    "";
  const canSelectVehicleType =
    !isRemainingItems &&
    !isReadOnly &&
    vehicleTypeOptions.length > 0 &&
    typeof onSelectedVehicleTypeChange === "function";
  const showVehicleType =
    !isRemainingItems &&
    (vehicleTypeOptions.length > 0 || supplier.vehicleType !== undefined);

  return (
    <Box
      bg="gray.50"
      px={4}
      py={3}
      borderBottom="1px solid"
      borderColor="orange.200"
    >
      <Flex justify="space-between" align="center">
        <HStack spacing={3} align="flex-start">
          <VStack align="flex-start" spacing={0}>
            <Text
              fontSize="0.95rem"
              fontWeight="800"
              color="gray.700"
              fontFamily="'Nunito','Inter', sans-serif"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              {displayTitle}
            </Text>
            {!isRemainingItems && supplier.supplierCity && (
              <HStack spacing={1}>
                <MdLocationOn size="12" color="#3b82f6" />
                <Text
                  fontSize="0.75rem"
                  color="gray.600"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                >
                  {supplier.supplierCity}
                </Text>
              </HStack>
            )}
          </VStack>
          <HStack spacing={2} align="center">
            {!isRemainingItems && supplier.distanceKm !== undefined && (
              <HStack
                spacing={1}
                bg="white"
                px={2}
                py={0.5}
                borderRadius="full"
                border="1px solid"
                borderColor="gray.200"
              >
                <MdLocationOn size="12" color="#3b82f6" />
                <Text
                  fontSize="0.7rem"
                  color="gray.500"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                >
                  {supplier.distanceKm.toFixed(1)} km
                </Text>
              </HStack>
            )}
            {showVehicleType &&
              (canSelectVehicleType ? (
                <Menu>
                  <MenuButton
                    as={Button}
                    size="xs"
                    variant="outline"
                    bg="white"
                    px={2}
                    py={0.5}
                    h="auto"
                    minH="25px"
                    borderRadius="full"
                    border="1px solid"
                    borderColor="gray.200"
                    fontWeight="700"
                    rightIcon={<ChevronDownIcon boxSize={3} />}
                  >
                    <HStack spacing={1}>
                      <Text
                        fontSize="0.7rem"
                        color="gray.500"
                        fontFamily="'Nunito','Inter', sans-serif"
                        fontWeight="700"
                      >
                        {selectedLabel}
                      </Text>
                      {selectedIcon && (
                        <Icon as={selectedIcon} color="blue.500" boxSize={3} />
                      )}
                    </HStack>
                  </MenuButton>
                  <MenuList minW="160px" py={1} zIndex={20}>
                    {vehicleTypeOptions.map((entry: VehicleTypeCost) => (
                      <MenuItem
                        key={entry.vehicleType}
                        fontSize="0.75rem"
                        fontWeight={
                          entry.vehicleType === selectedVehicleType
                            ? "700"
                            : "500"
                        }
                        onClick={() =>
                          onSelectedVehicleTypeChange?.(entry.vehicleType)
                        }
                      >
                        {entry.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              ) : (
                <HStack
                  spacing={1}
                  bg="white"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="gray.200"
                  minH="25px"
                >
                  <Text
                    fontSize="0.7rem"
                    color="gray.500"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="700"
                  >
                    {selectedLabel}
                  </Text>
                  {selectedIcon && (
                    <Icon as={selectedIcon} color="blue.500" boxSize={3} />
                  )}
                </HStack>
              ))}
            <HStack
              spacing={1}
              bg="white"
              px={2}
              py={0.5}
              borderRadius="full"
              border="1px solid"
              borderColor="gray.200"
              minH="25px"
            >
              <Text
                fontSize="0.7rem"
                color={isRemainingItems ? "red.700" : "gray.500"}
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="700"
              >
                MRP: ₹{supplierFulfillingItemsMRP.toLocaleString("en-IN")}
              </Text>
            </HStack>
          </HStack>
        </HStack>
        {actions}
      </Flex>
    </Box>
  );
};
